import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, User } from 'lucide-react';
import { getPhaseClasses, formatPhaseName } from '../../lib/workbook/utils';
import { type Phase } from '../../lib/workbook/tokens';

const phases: { phase: Phase; description: string; pages: number }[] = [
  { phase: 'decide', description: 'Commit to Prioritizing Intentionally', pages: 8 },
  { phase: 'engage', description: 'Align on Purpose, People, and Constraints', pages: 8 },
  { phase: 'gather', description: 'Capture Options & Evidence', pages: 8 },
  { phase: 'arrange', description: 'Make Sense, Compare, and Balance', pages: 8 },
  { phase: 'prioritize', description: 'Choose, Sequence, and Commit', pages: 8 },
];

const modes = [
  {
    icon: User,
    title: 'Solo Mode',
    description: 'Self-guided individual work',
    color: 'text-[var(--text-ter)]'
  },
  {
    icon: Users,
    title: 'Team Mode', 
    description: 'Collaborative team sessions',
    color: 'text-[var(--green)]'
  },
  {
    icon: BookOpen,
    title: 'Coach Mode',
    description: 'Facilitated workshops',
    color: 'text-[var(--text-ter)]'
  }
];

export default function WorkbookHome() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-[var(--bg-raised)] border-b border-[var(--rule)]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[var(--text-pri)]">
                Managing Priorities Workbook
              </h1>
              <p className="text-[var(--text-sec)]">
                Interactive DEGAP Process Guide
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-[var(--text-ter)]">Digital Edition</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[var(--text-pri)] mb-4">
            The DEGAP Process
          </h2>
          <p className="text-xl text-[var(--text-sec)] max-w-3xl mx-auto">
            A systematic approach to prioritization that works when it matters most. 
            Five phases, endless applications.
          </p>
        </div>

        {/* DEGAP Phases */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-[var(--text-pri)] mb-8 text-center">
            The Five Phases
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {phases.map(({ phase, description, pages }) => {
              const phaseClasses = getPhaseClasses(phase, 50);
              const textColor = getPhaseClasses(phase, 600).color;
              
              return (
                <Link
                  key={phase}
                  to={`/elevation/workbook/${phase}`}
                  className="group block h-full"
                >
                  <div 
                    className="h-full p-6 border-2 border-transparent hover:border-[var(--rule)] transition-all duration-200 group-hover:shadow-lg flex flex-col"
                    style={phaseClasses}
                  >
                    <div className="flex items-center justify-center mb-4">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold"
                        style={{ backgroundColor: textColor }}
                      >
                        {phase.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <h4 
                      className="text-lg font-semibold mb-2 text-center"
                      style={{ color: textColor }}
                    >
                      {formatPhaseName(phase)}
                    </h4>
                    <p className="text-sm text-center opacity-90 mb-3 flex-grow">
                      {description}
                    </p>
                    <div className="text-xs text-center opacity-75">
                      {pages} pages
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Usage Modes */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-[var(--text-pri)] mb-8 text-center">
            How to Use This Workbook
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {modes.map(({ icon: Icon, title, description, color }) => (
              <div key={title} className="text-center">
                <div className="flex justify-center mb-4">
                  <div className={`p-4 rounded-full bg-[var(--hover-tint)] ${color}`}>
                    <Icon className="w-8 h-8" />
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-[var(--text-pri)] mb-2">
                  {title}
                </h4>
                <p className="text-[var(--text-sec)]">
                  {description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Start */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-8">
          <h3 className="text-2xl font-bold text-[var(--text-pri)] mb-4">
            Quick Start
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-semibold text-[var(--text-pri)] mb-3">
                For Individuals
              </h4>
              <ol className="space-y-2 text-[var(--text-sec)]">
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--text-ter)]">1.</span>
                  <span>Start with Decide → Priority Inventory</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--text-ter)]">2.</span>
                  <span>Work through each phase sequentially</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--text-ter)]">3.</span>
                  <span>Use Coach Rails for guidance</span>
                </li>
              </ol>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-[var(--text-pri)] mb-3">
                For Teams
              </h4>
              <ol className="space-y-2 text-[var(--text-sec)]">
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">1.</span>
                  <span>Begin with individual inventories</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">2.</span>
                  <span>Compare and discuss differences</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">3.</span>
                  <span>Use timing guides for sessions</span>
                </li>
              </ol>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <Link
              to="/elevation/workbook/decide"
              className="inline-flex items-center gap-2 bg-[var(--slate)] text-white px-6 py-3 font-semibold hover:bg-[var(--slate)] transition-colors"
            >
              Start with Decide
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

